import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { Button, message, Modal, Space, Tabs, Tag } from 'antd';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  AppointmentStatusType,
  APPOINTMENT_STATUSES,
  APPOINTMENT_STATUS_VALUES
} from 'constants/appointments';
import { FULL_DATE_FORMAT } from 'constants/dates';
import dayjs from 'dayjs';
import { useUIDispatchContext } from 'providers/UI';
import { PLAN_TYPE_VALUES } from 'constants/profile';
import { encodeToBase64 } from '@medicusunion/front-utils';
import {
  AppointmentDetailsProvider,
  useAppointmentDetails
} from 'providers/AppointmentDetails';
import {
  openRoom as openRoomRest,
  updateAppointmentStatus
} from 'services/api/appointments';
import {
  CalendarOutlined,
  FileDoneOutlined,
  FileImageOutlined,
  MessageOutlined,
  ReconciliationOutlined
} from '@ant-design/icons';

import {
  Details,
  Files,
  Reports,
  Notes,
  AddOptionModal,
  CallButton,
  AppointmentInfo,
  AddReportModal
  // CallPatient
} from './components';
import { Loading, PageWrapper } from 'components/ui';
import { getStatusColor } from 'utils/appointments';
import { showRestError } from 'utils/showErrorMessage';
import { getIdStart } from 'utils/string';
import { refreshTokenStorage, tokenStorage } from 'utils/storage';
import {
  ConfirmModalContent,
  CreateInvoiceModal,
  PatientHealthDetails
} from 'components/shared';

const AppointmentsDetailsContent: FC = () => {
  const { t, i18n } = useTranslation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { appointment, refresh } = useAppointmentDetails();

  const { toggleModal } = useUIDispatchContext();
  const [activeTab, setActiveTab] = useState('files');

  const openRoom = useCallback(() => {
    openRoomRest(params.id as string)
      .then(res => {
        const url = res.redirect_url;
        const roomId = res.meetUrl;
        const token = encodeToBase64(tokenStorage.get());
        const refreshToken = encodeToBase64(refreshTokenStorage.get());

        if (url) {
          const link = `${url}/home/${roomId}?t=${token}&r=${refreshToken}&lang=${i18n.language}`;

          window.location.href = link;
        }
      })
      .catch(err => showRestError(err));
  }, [params.id, i18n.language]);

  const updateStatus = useCallback(
    (status: number) => {
      updateAppointmentStatus(params.id as string, status)
        .then(() => {
          message.success(t(`messages.successfully_updated`));
          refresh();
        })
        .catch(err => showRestError(err));
    },
    [params.id, refresh, t]
  );

  const breadcrumbs = [t('appointments.page_title'), getIdStart(params?.id)];

  const isVideoCall = useMemo(
    () => appointment?.plan.type === PLAN_TYPE_VALUES.video_call,
    [appointment?.plan.type]
  );

  const date = useMemo(() => {
    if (!isVideoCall || !appointment?.dates) return '';

    const accepted = appointment.dates.find(item => item.accepted);

    if (accepted) {
      return dayjs(accepted.date).format(FULL_DATE_FORMAT);
    }

    return '';
  }, [appointment?.dates, isVideoCall]);

  useEffect(() => {
    if (!date && isVideoCall) {
      setActiveTab('details');
    }
  }, [date, isVideoCall, searchParams]);

  const showJoinCall = useMemo(
    () =>
      isVideoCall &&
      appointment?.paid &&
      (appointment?.status === APPOINTMENT_STATUSES.approved ||
        appointment?.status === APPOINTMENT_STATUSES.started),
    [appointment?.paid, appointment?.status, isVideoCall]
  );

  const showStatusActions = useMemo(
    () => appointment?.status === APPOINTMENT_STATUSES.awaiting_confirmation,
    [appointment?.status]
  );

  const showCancel = useMemo(
    () => appointment?.status === APPOINTMENT_STATUSES.approved,
    [appointment?.status]
  );

  const onFinishCall = useCallback(() => {
    Modal.confirm({
      content: (
        <ConfirmModalContent
          title={t('appointments_details.have_you_finished')}
          message={t('appointments_details.dont_forget_to_submit')}
          okText={t('appointments.finish')}
          onOk={() => {
            Modal.destroyAll();
            updateStatus(APPOINTMENT_STATUSES.finished);
          }}
          onCancel={() => Modal.destroyAll()}
        />
      ),
      icon: null,
      footer: null
    });
  }, [params.id, t, updateStatus]);

  const extraButtons = useMemo(() => {
    const elements: ReactNode[] = [];

    if (showJoinCall) {
      elements.push(
        <Space key="join_call">
          <CallButton date={date} onJoinClick={() => openRoom()} />
          <Button size="large" onClick={onFinishCall}>
            {t('appointments.finish')}
          </Button>
        </Space>
      );
    }

    if (showStatusActions) {
      elements.push(
        <Button
          key="rejectAppointment"
          type="default"
          onClick={() =>
            toggleModal('rejectAppointment', true, {
              id: params.id as string,
              status: APPOINTMENT_STATUSES.rejected
            })
          }
          size="large"
          danger
        >
          {t('appointments_details.reject')}
        </Button>
      );

      if (appointment?.plan.type === PLAN_TYPE_VALUES.report) {
        elements.push(
          <Button
            key="acceptAppointment"
            onClick={() => updateStatus(APPOINTMENT_STATUSES.approved)}
            size="large"
            type="primary"
          >
            {t('appointments_details.accept')}
          </Button>
        );
      }
    }

    if (showCancel) {
      elements.push(
        <Button
          key="cancelAppointment"
          type="default"
          size="large"
          onClick={() =>
            toggleModal('rejectAppointment', true, {
              id: params.id as string,
              status: APPOINTMENT_STATUSES.canceled
            })
          }
          danger
        >
          {t('appointments_details.cancel')}
        </Button>
      );
    }

    if (
      appointment?.status === APPOINTMENT_STATUSES.completed &&
      !appointment?.doctor_invoice?.id
    ) {
      elements.push(
        <CreateInvoiceModal
          key="CreateInvoiceModal"
          appointmentId={params.id as string}
        />
      );
    }

    // if (appointment?.patient.id) {
    //   elements.push(
    //     <CallPatient key="CallPatient" patientId={appointment.patient.id} />
    //   );
    // }

    return elements;
  }, [
    showJoinCall,
    showStatusActions,
    showCancel,
    appointment?.status,
    appointment?.doctor_invoice?.id,
    // appointment?.patient.id,
    appointment?.plan.type,
    date,
    onFinishCall,
    t,
    openRoom,
    params.id,
    toggleModal,
    updateStatus
  ]);

  const showReports =
    appointment?.status === APPOINTMENT_STATUSES.finished ||
    appointment?.status === APPOINTMENT_STATUSES.completed ||
    (!isVideoCall && appointment?.paid);

  const showHealthDetails = Object.keys(
    appointment?.patient?.health_info || {}
  ).length;

  if (!appointment) return <Loading />;

  return (
    <PageWrapper
      title={t('appointments_details.page_title')}
      breadcrumbs={breadcrumbs}
      badge={
        <>
          <Tag
            color={getStatusColor(appointment.status)}
            style={{ marginLeft: 16 }}
          >
            {t(
              `appointments.${
                APPOINTMENT_STATUS_VALUES[
                  appointment.status as AppointmentStatusType
                ]
              }`
            )}
          </Tag>
          {appointment.admin_paid && (
            <Tag color="success">{t('appointments_details.paid')}</Tag>
          )}
          {appointment?.doctor_invoice?.id && (
            <Tag color="lime">{t('appointments_details.invoice_sent')}</Tag>
          )}
        </>
      }
      color="white"
      extra={extraButtons}
      back
    >
      <AppointmentInfo date={date} />
      <Tabs
        activeKey={activeTab}
        onChange={e => setActiveTab(e)}
        items={[
          ...(date || !isVideoCall
            ? []
            : [
                {
                  label: (
                    <div>
                      <CalendarOutlined style={{ marginRight: 8 }} />
                      {t('appointments_details.appointment_tab_1')}
                    </div>
                  ),
                  key: 'details',
                  style: { paddingBottom: 40 },
                  children: <Details date={date} isVideoCall={isVideoCall} />
                }
              ]),
          {
            label: (
              <div>
                <FileImageOutlined style={{ marginRight: 8 }} />
                {t('appointments_details.appointment_tab_2')}
              </div>
            ),
            key: 'files',
            style: { paddingBottom: 40 },
            children: <Files />
          },
          ...(showHealthDetails && appointment?.patient
            ? [
                {
                  label: (
                    <div>
                      <ReconciliationOutlined style={{ marginRight: 8 }} />
                      {t('appointments_details.health_details')}
                    </div>
                  ),
                  key: 'health',
                  style: { paddingBottom: 40 },
                  children: <PatientHealthDetails data={appointment.patient} />
                }
              ]
            : []),
          ...(showReports
            ? [
                {
                  label: (
                    <div>
                      <FileDoneOutlined style={{ marginRight: 8 }} />
                      {t('appointments_details.appointment_tab_3')}
                    </div>
                  ),
                  key: 'reports',
                  style: { paddingBottom: 40 },
                  children: <Reports />
                }
              ]
            : []),
          {
            label: (
              <div>
                <MessageOutlined style={{ marginRight: 8 }} />
                {t('appointments_details.appointment_tab_5')}
              </div>
            ),
            key: 'notes',
            style: { paddingBottom: 40 },
            children: <Notes />
          }
        ]}
      />
      <AddOptionModal />
      <AddReportModal />
    </PageWrapper>
  );
};

const AppointmentsDetails: FC = () => {
  const params = useParams();

  return (
    <AppointmentDetailsProvider id={params.id}>
      <AppointmentsDetailsContent />
    </AppointmentDetailsProvider>
  );
};

export default AppointmentsDetails;
